import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';

/**
 * Keeps the sold-out "Find similar styles" link in sync with the selected variant.
 *
 * The block (blocks/_ap-sold-out-similar-row.liquid) renders its own correct state on
 * the server, so first paint and the no-JS case are already right. On every variant
 * change the variant picker re-fetches the product section and emits it as
 * `event.detail.data.html`; we simply morph this element from its freshly rendered
 * counterpart in that HTML. That keeps all the eligibility, collection-resolver and
 * label logic server-side — this script never recomputes the link, it only adopts the
 * server's answer. Mirrors local-pickup.js.
 *
 * Analytics: dispatches `ap:similar-styles-shown` on each hidden -> visible transition
 * (an impression — the customer just picked a size we can't sell) and
 * `ap:similar-styles-click` when the link is followed. ap-variant-analytics.js
 * translates both into `ap_similar_styles_*` Web Pixels events, pairing them
 * with its `ap_size_selected` unmet-demand signal.
 */
class ApSoldOutSimilarStyles extends Component {
  /** @type {AbortController | undefined} */
  #listeners;

  /** @type {boolean} */
  #linkVisible = false;

  connectedCallback() {
    super.connectedCallback();

    this.#listeners = new AbortController();
    this.closest('.shopify-section, dialog')?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal: this.#listeners.signal,
    });
    this.addEventListener('click', this.#onClick, { signal: this.#listeners.signal });

    // A ?variant= deep link to a sold-out variant renders the link visible on first
    // paint — an impression no variant:update will ever report.
    this.#linkVisible = this.#isLinkVisible();
    if (this.#linkVisible) this.#dispatch('ap:similar-styles-shown');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#listeners?.abort();
  }

  /** @param {import('@theme/events').VariantUpdateEvent} event */
  #onVariantUpdate = (event) => {
    const html = event.detail?.data?.html;
    const blockId = this.dataset.blockId;
    if (!html || !blockId) return;

    // Find this block's re-rendered counterpart for the newly selected variant.
    const next = html.querySelector(`ap-sold-out-similar-styles[data-block-id="${CSS.escape(blockId)}"]`);
    if (next) morph(this, next);

    // Impression = each hidden -> visible transition, read after the morph so the
    // payload reflects the size that was just selected.
    const visible = this.#isLinkVisible();
    if (visible && !this.#linkVisible) this.#dispatch('ap:similar-styles-shown');
    this.#linkVisible = visible;
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('a')) return;
    this.#dispatch('ap:similar-styles-click');
  };

  #isLinkVisible() {
    const link = this.querySelector('a');
    return !!link && !link.hasAttribute('hidden');
  }

  /** @param {string} name */
  #dispatch(name) {
    const link = this.querySelector('a');
    this.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        detail: {
          productId: this.dataset.productId,
          productUrl: this.dataset.productUrl,
          sizeValue: link?.dataset.sizeValue,
          sizeLabel: link?.dataset.sizeLabel,
          variantId: link?.dataset.variantId,
          targetUrl: link?.getAttribute('href'),
        },
      })
    );
  }
}

if (!customElements.get('ap-sold-out-similar-styles')) {
  customElements.define('ap-sold-out-similar-styles', ApSoldOutSimilarStyles);
}
